import "../../assets/sass/components/signUp/_athleteSignUp.scss";

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import { Trans, useTranslation } from "react-i18next";
import debounce from "lodash/debounce";

import GLOBAL from "../../global";
import { getUserIdentification } from "../../utils/Utils";
import PasswordStrengthChecker from "./PasswordStrengthChecker";
import authService from "../../services/AuthService";
import useForm from "../../hooks/useForm";
import AnswerValidationService from "../../services/answerValidation/AnswerValidationService";
import answerValidationMessagesConstant from "../../services/answerValidation/answerValidationMessagesConstant";
import CONSTANT from "../../constants/Constant";
import useD2C from "../../hooks/useD2C";
import { useHandleDirectPayment } from "../../contexts/DirectPaymentContext";
import { setEventData } from "../../features/askEmma/askEmmaSlice";
import { useDispatch } from "react-redux";
import InputMask from "react-input-mask";
import routes from "../../constants/Routes";
import { Link } from "react-router-dom";
import DirectToConsumerService from "../../services/DirectToConsumerService";
import { useTenantFeatures } from "../../features/tenantFeatures/useTenantFeature";

const USER_STATUS = Object.freeze({
    REGISTERED: 1,
    INVITED: 2,
    NOT_FOUND: 3,
    D2C_REGISTRATION: 4
});

function AthleteSignUp(props) {
    const { onSubmitSignUp, invalidFormElement, onClickTermOfServiceToggle, signUpCreated, errorMessage, setErrorMessage, onClickPrivacyPolicyToggle, isD2CRegistration } = props;
    const { IsD2CEnabled } = useD2C();
    const { isLoading: isDirectPaymentLoading, email: directPaymentUserEmail } = useHandleDirectPayment();
    const dispatch = useDispatch();
    const [passwordStrength, setPasswordStrength] = useState(false);
    const [showPasswordStrength, setShowPasswordStrength] = useState('');
    const { i18n } = useTranslation();
    const location = useLocation();
    const passwordRef1 = useRef();
    const passwordRef2 = useRef();
    const [sessionIdFromUrl, setSessionIdFromUrl] = useState('');
    const [pgIdFromUrl, setPgIdFromUrl] = useState('');
    const [isBillingEmailReadonly, setIsBillingEmailReadonly] = useState(false);
    const [inputType, setInputType] = useState({ password: true, confirmPassword: true })
    const togglePassword = useRef(false);
    const subscription = useRef(true);
    const [validUser, setValidUser] = useState(0);
    const [checkingInvitation, setCheckingInvitation] = useState(false);
    const { isAthleteEnabled } = useTenantFeatures();

    const {
        isNewUserFromDirectPayment,
        setIsNewUserFromDirectPayment,
        clearDirectPaymentState,
    } = useHandleDirectPayment();

    const validateField = (name, value) => {
        let validationResult;
        switch (name) {
            case 'FirstName':
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.FIRST_NAME, answerValidationMessagesConstant.FIRST_AND_LAST_NAME.KEY, value);
            case 'LastName':
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.LAST_NAME, answerValidationMessagesConstant.FIRST_AND_LAST_NAME.KEY, value);
            case 'Email':
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.EMAIL, CONSTANT.ELEMENT_TYPES.EMAIL, value);
            case 'BillingEmail':
                if (!sessionIdFromUrl || !pgIdFromUrl) return { valid: true };
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.EMAIL, CONSTANT.ELEMENT_TYPES.EMAIL, value);
            case 'Password':
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.PASSWORD, answerValidationMessagesConstant.PASSWORD.KEY, value);
            case 'ConfirmPassword':
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.CONFIRM_PASSWORD, answerValidationMessagesConstant.CONFIRM_PASSWORD.KEY, { value, password: values.Password });
            case 'dateOfBirth':
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.DATE_OF_BIRTH, answerValidationMessagesConstant.DATE_OF_BIRTH.KEY, value);
            case 'AcceptTerms':
                return AnswerValidationService.validate(CONSTANT.CHATBOT_ACCOUNT.TERMS_AND_CONDITIONS, answerValidationMessagesConstant.TERMS_AND_CONDITIONS.KEY, value);
            default:
                return { valid: true, error: null };
        }
    };

    const initialValuesForSignUp = {
        FirstName: "",
        LastName: "",
        dateOfBirth: "",
        Email: GLOBAL.URL_PARAMS.EMAIL_ADDRESS ? GLOBAL.URL_PARAMS.EMAIL_ADDRESS : '',
        BillingEmail: '',
        Password: "",
        ConfirmPassword: "",
        HearAboutUs: "",
        TimeZone: "Eastern Standard Time",
        AcceptTerms: false,
        UseSameEmailForAthleteAccount: false,
        SessionId: "",
    };

    const { handleChange, handleBlur, handleSubmit, setFieldValue, values, errors, touched, isSubmitting, setIsSubmitting } = useForm(initialValuesForSignUp, onSubmitSignUp, validateField);

    useEffect(() => {
        const url = window.location.href;
        const params = new URLSearchParams(url.slice(url.indexOf('?')));
        const InvGId = params.get('InvGId') || params.get('invgid');
        const sessionId = params.get('session_id');
        const pgId = params.get('pgid');

        if (sessionId && pgId) {
            setSessionIdFromUrl(sessionId);
            setPgIdFromUrl(pgId);
            setIsBillingEmailReadonly(true);
        }

        if (InvGId) {
            handleVerifyInvitation(InvGId);
        }

        const billingEmailFromUrl = params.get('billing_email');
        if (billingEmailFromUrl) {
            setFieldValue('BillingEmail', billingEmailFromUrl);
            setFieldValue('Email', billingEmailFromUrl);
            setFieldValue('UseSameEmailForAthleteAccount', true);
            handleVerifyEmail(billingEmailFromUrl, null);
        }

        dispatch(setEventData({ eventName: 'patientPortal_Registration' }))
    }, []);

    useEffect(() => {
        if (directPaymentUserEmail) {
            setFieldIfValid('BillingEmail', directPaymentUserEmail);
        }
    }, [directPaymentUserEmail]);

    const handleClick = e => {
        if (passwordRef1?.current?.contains(e.target)) {
            setPasswordStrength(true);
            return;
        }
        if (!togglePassword.current) {
            setPasswordStrength(false);
        }
        togglePassword.current = false;
    };

    useEffect(() => {
        document.addEventListener("keypress", handleClick);
        document.addEventListener("click", handleClick);
        return () => {
            subscription.current = false;
            document.removeEventListener("keypress", handleClick);
            document.removeEventListener("click", handleClick);
        };
    }, []);

    const passwordStrengthChecker = (e) => {
        e.preventDefault();
        setShowPasswordStrength(e.target.value);
    };

    const onClickChangeInputType = (passwordType) => {
        setInputType({ ...inputType, [passwordType]: !inputType[passwordType] })
    }

    const handleVerifyEmail = debounce(async (value, errorMessage) => {
        if (errorMessage || !value || checkingInvitation) return;

        setCheckingInvitation(true);
        let isRedirecting = false;
        try {
            const payload = { EmailAddress: value, IsD2CRegister: isD2CRegistration }
            const response = await authService.verifyEmail(payload);
            const { data, success, error } = response;

            if (!success) {
                setErrorMessage(error);
                return;
            }
            setErrorMessage('');

            const { VerifyStatus, InvitationId, VerifyStatusText } = data || {};

            if (VerifyStatusText === "Registered" && values.BillingEmail) {
                isRedirecting = true;
                setCheckingInvitation(true);
                // Set valid user to show the message
                setValidUser(USER_STATUS.REGISTERED);

                // Call props methods if they exist (for backward compatibility)
                if (props?.setEmailToLoadInitiallyAtSignIn) {
                    props?.setEmailToLoadInitiallyAtSignIn(value);
                }

                // Clear direct payment state to prevent redirect loop
                clearDirectPaymentState();

                // Wait for 3 seconds to show the message
                await new Promise(resolve => setTimeout(resolve, 3000));

                // Redirect to sign-in with query params using window.location for immediate redirect
                const search = location.search;
                const queryString = search.includes('?') ? search.split('?')[1] : '';
                const params = new URLSearchParams(queryString);
                params.set('redirect_from', 'sign_up');
                // Add BillingEmail as query parameter if it exists
                if (values?.BillingEmail) {
                    params.set('billing_email', values.BillingEmail);
                }
                window.location.href = `${routes.signIn}?${params.toString()}`;

                return;
            }

            if (VerifyStatus === USER_STATUS.REGISTERED && props?.setEmailToLoadInitiallyAtSignIn) {
                isRedirecting = true;
                setCheckingInvitation(true);
                await new Promise(resolve => setTimeout(resolve, 3000));

                props?.setEmailToLoadInitiallyAtSignIn(value)
                props.handleOnClickAreYouRegistered();
                return;
            }

            setFieldValues(data, setFieldValue);
            GLOBAL.URL_PARAMS.INVITATION_ID = InvitationId || null;
            setValidUser(VerifyStatus);
            setIsNewUserFromDirectPayment(VerifyStatus === USER_STATUS.D2C_REGISTRATION);
        } finally {
            if (!isRedirecting) {
                setCheckingInvitation(false);
            }
        }
    }, 800);

    const handleVerifyInvitation = async (invitationId) => {
        if (!invitationId) return;

        setCheckingInvitation(true);
        try {
            const payload = { InvGId: invitationId }
            const response = await authService.verifyInvitation(payload);
            const { data, success, error } = response;
            setCheckingInvitation(false);

            if (!success) {
                setErrorMessage(error);
                return;
            }

            setFieldValues(data, setFieldValue)
            GLOBAL.URL_PARAMS.INVITATION_ID = invitationId || null;
            setValidUser(data.VerifyStatus);
        } finally {
            setCheckingInvitation(false);
        }
    };

    const setFieldIfValid = (field, value) => value != null && setFieldValue(field, value);

    const setFieldValues = (data, setFieldValue) => {
        const {
            EmailAddress: email = '',
            FirstName: firstName = '',
            LastName: lastName = '',
        } = data || {};

        email && setFieldIfValid('Email', email);
        firstName && setFieldIfValid('FirstName', firstName);
        lastName && setFieldIfValid('LastName', lastName);
    };

    const shouldSignUpBtnDisabled = () => {
        if (isSubmitting) return true;
        if (validUser === USER_STATUS.REGISTERED) return true;
        return false;
    }

    return (
        <div className="athlete-signup-section">
            <div className="athlete-signup-title">
                Unlock Your Athletic Potential
            </div>

            <div className="athlete-signup-content-container">
                <div className="athlete-signup-content-left">
                    <img
                        src="/assets/images/create-account-gear.png"
                        alt="Create Account"
                    />
                </div>

                <div className="athlete-signup-content-right">
                    <form onSubmit={handleSubmit} autoComplete="off">
                        {/* Status Messages */}
                        <div className="margin-bottom-1-rem">
                            {validUser === USER_STATUS.NOT_FOUND && !isAthleteEnabled && (
                                <div className="signup-alert signup-alert-danger" role="alert">
                                    <div className="alert-text">Public registration is disabled for now, Please contact
                                        with your provider.
                                    </div>
                                </div>
                            )}
                            {validUser === USER_STATUS.REGISTERED && (
                                <div className="signup-alert signup-alert-danger" role="alert">
                                    <p className="alert-text error-message">
                                        You’re a registered user, please log in with your email and password.
                                    </p>
                                </div>
                            )}
                            {validUser === USER_STATUS.INVITED && (
                                <div className="signup-alert signup-alert-success" role="alert">
                                    <div className="alert-text">You have been invited to register by your provider.
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <p className={`z-10 position-center font-bold text-white font-size-md ${checkingInvitation ? " display-block " : " display-none "}`}>Verifying your invitation please wait...</p>
                            <div className={`${checkingInvitation ? "blurry pointer-none cursor-not-allowed " : ""}`}>

                                {/* Billing Section (Conditional) */}
                                {sessionIdFromUrl && pgIdFromUrl && (
                                    <>
                                        {/* Billing Email */}
                                        <div className="athlete-signup-input-group">
                                            <label className="athlete-signup-label">Card Holder E-mail</label>
                                            <div className="athlete-input-bg">
                                                <input
                                                    className={`athlete-signup-input ${directPaymentUserEmail || isBillingEmailReadonly ? 'disabled-input' : ''}`}
                                                    type="email"
                                                    placeholder="cardholderemail@email.com"
                                                    name="BillingEmail"
                                                    disabled={!!directPaymentUserEmail || isBillingEmailReadonly}
                                                    onChange={async (e) => {
                                                        await handleChange(e);
                                                        if (values.UseSameEmailForAthleteAccount) {
                                                            await setFieldValue('Email', e.target.value);
                                                        }
                                                    }}
                                                    onBlur={handleBlur}
                                                    value={values?.BillingEmail}
                                                />
                                            </div>
                                            {errors.BillingEmail && touched.BillingEmail && <div className="athlete-signup-error-msg">{errors.BillingEmail}</div>}
                                        </div>

                                        {/* Sync Checkbox */}
                                        <label className="athlete-signup-checkbox-label">
                                            <input
                                                type="checkbox"
                                                name="UseSameEmailForAthleteAccount"
                                                onChange={async (e) => {
                                                    await handleChange(e);
                                                    if (e.target.checked && values.BillingEmail) {
                                                        await setFieldValue('Email', values.BillingEmail);
                                                        const validationResult = validateField('Email', values.BillingEmail);
                                                        if (validationResult && validationResult.valid) {
                                                            await handleVerifyEmail(values.BillingEmail, null);
                                                        }
                                                    }
                                                }}
                                                checked={values.UseSameEmailForAthleteAccount}
                                            />
                                            <span className="checkmark"></span>
                                            <span className="athlete-signup-checkbox-text">Use the cardholder’s email as the login email.</span>
                                        </label>
                                    </>
                                )}

                                {/* Sign-in Email */}
                                <div className="athlete-signup-input-group">
                                    <label className="athlete-signup-label">Login Email</label>
                                    <div className="athlete-input-bg">
                                        <input
                                            className={`athlete-signup-input ${values.UseSameEmailForAthleteAccount ? 'disabled-input' : ''}`}
                                            type="email"
                                            placeholder="Enter Email"
                                            name="Email"
                                            disabled={values.UseSameEmailForAthleteAccount}
                                            onChange={handleChange}
                                            onBlur={async (e) => {
                                                await handleBlur(e);
                                                const validationResult = validateField('Email', e.target.value);
                                                if (validationResult && validationResult.valid) {
                                                    await handleVerifyEmail(e.target.value, null);
                                                }
                                            }}
                                            value={values?.Email}
                                        />
                                    </div>
                                    {errors.Email && touched.Email && <div className="athlete-signup-error-msg">{errors.Email}</div>}
                                    {errorMessage && <div className="athlete-signup-error-msg">{errorMessage}</div>}
                                </div>

                                {/* Names */}
                                <div className="athlete-signup-row">
                                    <div className="athlete-signup-col">
                                        <label className="athlete-signup-label">First Name</label>
                                        <div className="athlete-input-bg">
                                            <input
                                                className="athlete-signup-input"
                                                type="text"
                                                placeholder="First Name"
                                                name="FirstName"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.FirstName}
                                                maxLength="30"
                                            />
                                        </div>
                                        {errors.FirstName && touched.FirstName && <div className="athlete-signup-error-msg">{errors.FirstName}</div>}
                                    </div>
                                    <div className="athlete-signup-col">
                                        <label className="athlete-signup-label">Last name</label>
                                        <div className="athlete-input-bg">
                                            <input
                                                className="athlete-signup-input"
                                                type="text"
                                                placeholder="Last Name"
                                                name="LastName"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.LastName}
                                                maxLength="25"
                                            />
                                        </div>
                                        {errors.LastName && touched.LastName && <div className="athlete-signup-error-msg">{errors.LastName}</div>}
                                    </div>
                                </div>

                                {/* DOB */}
                                <div className="athlete-signup-input-group">
                                    <label className="athlete-signup-label">Birth Month date</label>
                                    <div className="athlete-input-bg">
                                        <InputMask
                                            mask="99/9999"
                                            maskChar="_"
                                            className="athlete-signup-input"
                                            type="text"
                                            name="dateOfBirth"
                                            placeholder="MM/YYYY"
                                            value={values.dateOfBirth}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </div>
                                    {errors.dateOfBirth && touched.dateOfBirth && !errors.dateOfBirth.includes("under the age of 13") && (
                                        <div className="athlete-signup-error-msg">{errors.dateOfBirth}</div>
                                    )}
                                </div>

                                {errors.dateOfBirth && touched.dateOfBirth && errors.dateOfBirth.includes("under the age of 13") ? (
                                    /* Age Alert shown in place of password and submit */
                                    <div className="athlete-signup-age-alert">
                                        <div className="athlete-signup-age-alert-content">
                                            <i className="fa fa-exclamation-triangle" />
                                            <p>
                                                Individuals under the age of 13 are prohibited from registering for Atlas Athlete. For more information, reach out to <a href="mailto:support@atlasathlete.ai">support@atlasathlete.ai</a>
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Passwords */}
                                        <div className="athlete-signup-row">
                                            <div className="athlete-signup-col">
                                                <label className="athlete-signup-label">Password</label>
                                                <div className="athlete-password-input-wrapper">
                                                    <input
                                                        className="athlete-signup-input"
                                                        ref={passwordRef1}
                                                        type={inputType.password ? "password" : "text"}
                                                        placeholder=""
                                                        name="Password"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={values.Password}
                                                        onKeyUp={(e) => passwordStrengthChecker(e)}
                                                    />
                                                    <span className="athlete-password-toggle-icon">
                                                        {values.Password && (
                                                            <i onClick={() => onClickChangeInputType("password")} className={`fa ${inputType.password ? 'fa-eye' : 'fa-eye-slash'}`} />
                                                        )}
                                                    </span>
                                                </div>
                                                {errors.Password && touched.Password && <div className="athlete-signup-error-msg">{errors.Password}</div>}
                                                {passwordStrength && showPasswordStrength && (
                                                    <PasswordStrengthChecker password={showPasswordStrength} className="athlete-password-strength" />
                                                )}
                                            </div>
                                            <div className="athlete-signup-col">
                                                <label className="athlete-signup-label">Confirm Password</label>
                                                <div className="athlete-password-input-wrapper">
                                                    <input
                                                        className="athlete-signup-input"
                                                        ref={passwordRef2}
                                                        type={inputType.confirmPassword ? "password" : "text"}
                                                        placeholder=""
                                                        name="ConfirmPassword"
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        value={values.ConfirmPassword}
                                                    />
                                                    <span className="athlete-password-toggle-icon">
                                                        {values.ConfirmPassword && (
                                                            <i onClick={() => onClickChangeInputType("confirmPassword")} className={`fa ${inputType.confirmPassword ? 'fa-eye' : 'fa-eye-slash'}`} />
                                                        )}
                                                    </span>
                                                </div>
                                                {errors.ConfirmPassword && touched.ConfirmPassword && <div className="athlete-signup-error-msg">{errors.ConfirmPassword}</div>}
                                            </div>
                                        </div>

                                        {/* Terms */}
                                        <label className="athlete-signup-checkbox-label">
                                            <input
                                                type="checkbox"
                                                name="AcceptTerms"
                                                onChange={handleChange}
                                                checked={values.AcceptTerms}
                                            />
                                            <span className="checkmark"></span>
                                            <span className="athlete-signup-terms-text">
                                                I agree to the <span className="terms-link" onClick={(e) => { e.preventDefault(); onClickTermOfServiceToggle(e) }}>Terms and Conditions</span>/ <span className="terms-link" onClick={(e) => { e.preventDefault(); onClickPrivacyPolicyToggle(e) }}>Privacy Policy</span>.
                                            </span>
                                        </label>
                                        {errors.AcceptTerms && touched.AcceptTerms && <div className="athlete-signup-error-msg athlete-signup-error-inline">{errors.AcceptTerms}</div>}

                                        {/* Submit */}
                                        <button
                                            type="submit"
                                            className="athlete-signup-submit-button"
                                            disabled={shouldSignUpBtnDisabled()}
                                        >
                                            {isSubmitting ? (
                                                <><i className="fa fa-spinner fa-spin" />&nbsp;&nbsp;Please wait...</>
                                            ) : (
                                                "Submit"
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </form>
                    {/*{errorMessage && <div className="athlete-signup-error-msg">{errorMessage}</div>}*/}
                    {!(isAthleteEnabled && sessionIdFromUrl && pgIdFromUrl) && (
                        <div className="athlete-signup-footer-text">
                            Are you already a registered user?
                            <span className="signin-link" onClick={props.handleOnClickAreYouRegistered}>Sign in.</span>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

export default AthleteSignUp;
